import * as React from 'react';
import clsx from 'clsx';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import FilterListIcon from '@material-ui/icons/FilterList';
import {Chip} from "@material-ui/core";



function descendingComparator(a: any, b: any, orderBy: any) {
    if(orderBy === 'availableForReplay') {
        if ((b[orderBy] || false) < (a[orderBy] || false)) {
            return -1;
        }
        if ((b[orderBy] || false) > (a[orderBy] || false)) {
            return 1;
        }
        return 0;
    } else {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    }
}

function getComparator(order: any, orderBy: any) {
    return order === 'desc'
        ? (a: any, b: any) => descendingComparator(a, b, orderBy)
        : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

function stableSort(array: any, comparator: any) {
    const stabilizedThis = array.map((el: any, index: any) => [el, index]);
    stabilizedThis.sort((a: any, b: any) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) { return order };
        return a[1] - b[1];
    });
    return stabilizedThis.map((el: any) => el[0]);
}

function EnhancedTableHead(props: any) {
    const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property: any, sort: boolean) => (event: any) => {
        if(sort) {
            onRequestSort(event, property);
        }
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    {
                        props.allowSelectAll &&
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={onSelectAllClick}
                            inputProps={{ 'aria-label': 'select all desserts' }} />
                    }
                </TableCell>
                <TableCell
                    align={'left'}
                    padding={'default'}
                    sortDirection={orderBy === 'id' ? order : false} >
                    <TableSortLabel
                        active={orderBy === 'id'}
                        direction={orderBy === 'id' ? order : 'asc'}
                        onClick={createSortHandler('id', true)}>
                        ID
                        {orderBy === 'id' ? (
                            <span className={classes.visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </span>
                        ) : null}
                    </TableSortLabel>
                </TableCell>
            {props.headCells.map((headCell: ColumnDescriptor) => (
                <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'default'}
                    sortDirection={orderBy === headCell.id ? order : false} >
                        {
                            headCell.sort ? (
                                <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={orderBy === headCell.id ? order : 'asc'}
                                    onClick={createSortHandler(headCell.id, headCell.sort)}>
                                        {headCell.label}
                                        {orderBy === headCell.id ? (
                                            <span className={classes.visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </span>
                                        ) : null}
                                </TableSortLabel>
                            ) : (
                                headCell.label
                            )
                        }
                </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

interface EnhancedTableHeadProps {
    classes: any,
    numSelected: number,
    onRequestSort: any,
    onSelectAllClick: any,
    order: 'asc' | 'desc',
    orderBy: string,
    rowCount: number,
}

const useToolbarStyles = makeStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
    title: {
        flex: '1 1 100%',
    },
}));


interface EnhancedTableToolbarProps {
    numSelected: number;
    allowDelete: boolean;
    allowFilter: boolean;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
    const classes = useToolbarStyles();
    const { numSelected } = props;

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >

        {numSelected > 0 ? (
            <Typography className={classes.title} color="inherit" variant="subtitle1">
            {numSelected} selected
        </Typography>
        ) : (
            <Typography className={classes.title} variant="h6" id="tableTitle">
            Classes
            </Typography>
        )}

        {
            numSelected > 0 && props.allowDelete &&
            <Tooltip title="Delete">
            <IconButton aria-label="delete">
            <DeleteIcon />
            </IconButton>
            </Tooltip>
        }
        {
            numSelected === 0 && props.allowFilter &&
            <Tooltip title="Filter list">
                <IconButton aria-label="filter list">
                    <FilterListIcon/>
                </IconButton>
            </Tooltip>
        }
    </Toolbar>
);
};



const useStyles = makeStyles(theme => ({
    root: {
        width: '90vw',
        paddingLeft: '5vw',
        paddingRight: '5vw'
    },
    paper: {
        width: '90vw',
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 750,
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    row: {
        cursor: 'pointer'
    }
}));

export interface ColumnDescriptor {
    type?: "image" | "text" | "timestamp" | "boolean";
    label: string;
    id: string;
    numeric?: boolean;
    disablePadding?: boolean;
    sort: boolean;
}

export interface TableRecord {
    id: string;
}

interface EnhancedTableProps<T extends TableRecord> {
    data: T[];
    columns: ColumnDescriptor[];
    allowSelectAll: boolean;
    onSelect: (ids: string[]) => void;
    allowDelete: boolean;
    allowFilter: boolean;
    dense: boolean;
}

export default function EnhancedTable<T extends TableRecord>(props: EnhancedTableProps<T>) {
    const classes = useStyles();
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('');
    const [selected, setSelected] = React.useState<string[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    const handleRequestSort = (event: any, property: any) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const handleSelectAllClick = (event: any) => {
        if (event.target.checked) {
            const newSelecteds = props.data.map(n => n.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: any, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: string[] = [];

        if(props.allowSelectAll) {
            // multiple select logic
            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, name);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1),
                );
            }
        } else {
           // single select logic
            if (selectedIndex === -1) {
                newSelected = [name];
            } else {
                newSelected = [];
            }
        }
        setSelected(newSelected);
        props.onSelect(newSelected);
    };

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (name: any) => selected.indexOf(name) !== -1;

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, props.data.length - page * rowsPerPage);

    return (
        <div className={classes.root}>
        <Paper className={classes.paper}>
        <EnhancedTableToolbar
            numSelected={selected.length}
            allowDelete={props.allowDelete}
            allowFilter={props.allowFilter}
        />
        <TableContainer>
        <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={props.dense ? 'small' : 'medium'}
            aria-label="enhanced table">
                <EnhancedTableHead
                    classes={classes}
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={props.data.length}
                    allowSelectAll={props.allowSelectAll}
                    headCells={props.columns}
                />
            <TableBody>
            {
                stableSort(props.data, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: T, index: any) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                        <TableRow
                            hover
                            onClick={(event: any) => handleClick(event, row.id)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.id}
                            className={classes.row}
                            selected={isItemSelected}>
                                <TableCell padding="checkbox">
                                <Checkbox
                                    checked={isItemSelected}
                                    inputProps={{ 'aria-labelledby': labelId }}/>
                                </TableCell>
                                <TableCell component="th" id={labelId} scope="row" padding="none">
                                    {row.id ? (
                                        row.id.indexOf("-") > -1 ? row.id.split('-')[0] : row.id
                                    ) : ""}
                                </TableCell>
                                    {
                                        props.columns.map( (column, i) => {
                                            if(column.type && column.type === "timestamp") {
                                                return <TableCell key={i} align="left">
                                                    {(new Date(row[column.id])).toLocaleString("en-US")}
                                                </TableCell>;
                                            } else if(column.type && column.type === "image") {
                                                return <TableCell key={i} align="left">
                                                    {<img src={row[column.id]} width={100}  />}
                                                </TableCell>;
                                            } else if(column.type && column.type === "boolean") {
                                                return <TableCell key={i} align="left">
                                                    {
                                                        row[column.id] ?
                                                            <Chip color="primary" label={"true"} /> :
                                                            <Chip color="secondary" label={"false"} />
                                                    }
                                                </TableCell>;
                                            } else {
                                                return <TableCell key={i} align="left">
                                                    {row[column.id]}
                                                </TableCell>;
                                            }
                                        })
                                    }
                        </TableRow>
                    );
                })
            }
            {
                emptyRows > 0 &&
                <TableRow style={{ height: (props.dense ? 33 : 53) * emptyRows }}>
                    <TableCell colSpan={6} />
                </TableRow>
            }
            </TableBody>
        </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={props.data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
        />
    </Paper>
    </div>
);
}


// WEBPACK FOOTER //
// ./src/components/common/table/EnhancedTable.tsx